import { useState, useEffect } from 'react'
import { useAuth } from '../context/AuthContext'
import api from '../utils/api'
import { formatDate } from '../utils/dateFormatter'
import './Orders.css'

const Orders = ({ embeddedAdmin = false }) => {
  const [orders, setOrders] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all') // User: all, pending, completed, etc. | Admin: active, old
  const [category, setCategory] = useState('tag') // tag or restaurant
  const [viewMode, setViewMode] = useState('user') // user or admin

  const { user, isAdmin } = useAuth()

  useEffect(() => {
    if (isAdmin() && embeddedAdmin) {
      setViewMode('admin')
      setFilter('active') // Default to active
      setCategory('tag') // Default to tags
      fetchOrders('active', 1, 'tag')
    } else {
      setViewMode('user')
      fetchOrders('my')
    }
  }, [user, embeddedAdmin]) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchOrders = async (type, page = 1, cat = category) => {
    setLoading(true)
    try {
      let endpoint = '/orders/my'

      if (viewMode === 'admin' || (isAdmin() && embeddedAdmin)) {
        endpoint = `/orders/admin?page=${page}&limit=${ordersPerPage}&category=${cat}`
        if (type && type !== 'all' && type !== 'my') {
          endpoint += `&view=${type}`
        }
      }

      console.log(`Fetching orders from: ${endpoint}`)
      const response = await api.get(endpoint)

      if (response.data.success) {
        setOrders(response.data.orders)
        if (response.data.pagination) {
          setTotalPages(response.data.pagination.pages)
          setTotalCount(response.data.pagination.total)
        } else {
          // Fallback for user view (client-side pagination)
          setTotalPages(Math.ceil(response.data.orders.length / ordersPerPage))
          setTotalCount(response.data.orders.length)
        }
      }
    } catch (error) {
      console.error('Error fetching orders:', error)
    } finally {
      setLoading(false)
    }
  }



  const handleAdminTabChange = (tab) => {
    setFilter(tab)
    fetchOrders(tab, 1, category)
  }

  const handleCategoryChange = (cat) => {
    setCategory(cat)
    setFilter('active')
    setCurrentPage(1)
    fetchOrders('active', 1, cat)
  }

  const handleUpdateStatus = async (orderId, newStatus) => {
    try {
      const response = await api.put(`/orders/${orderId}/status`, { status: newStatus })
      if (response.data.success) {
        // Refresh orders
        fetchOrders(filter, currentPage, category)
      }
    } catch (error) {
      console.error('Error updating status:', error)
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning'
      case 'shipped': return 'info'
      case 'delivered': return 'success'
      case 'cancelled': return 'danger'
      default: return 'secondary'
    }
  }

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [ordersPerPage] = useState(10)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)

  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage)
    if (viewMode === 'admin') {
      fetchOrders(filter, newPage, category)
    }
  }

  // Filter orders (Only for regular user view which is client-side)
  const filteredOrders = viewMode === 'admin' ? orders : orders.filter(order =>
    filter === 'all' ||
    (filter === 'completed' && ['delivered', 'cancelled'].includes(order.status)) ||
    (filter === 'pending' && ['pending', 'shipped'].includes(order.status))
  )

  // Display orders for current page
  const currentOrders = viewMode === 'admin'
    ? orders // Server already paginated
    : filteredOrders.slice((currentPage - 1) * ordersPerPage, currentPage * ordersPerPage)

  // Reset page when filter changes
  useEffect(() => {
    setCurrentPage(1)
    if (viewMode === 'admin' && orders.length > 0) {
      fetchOrders(filter, 1)
    }
  }, [filter, viewMode])

  // formatDate is imported from utils

  return (
    <div className={`${embeddedAdmin ? '' : 'container'} orders-page animate-fadeIn`}>
      <div className="page-header">
        <div>
          <h1>{viewMode === 'admin' ? 'Order Management' : 'My Orders'}</h1>
          <p className="page-subtitle">
            {viewMode === 'admin' ? 'Manage customer orders' : 'View and track your order history'}
          </p>
        </div>
      </div>

      <div className="orders-filters-container">
        {viewMode === 'admin' && (
          <div className="category-toggle">
            <button
              className={`cat-btn ${category === 'tag' ? 'active' : ''}`}
              onClick={() => handleCategoryChange('tag')}
            >
              Vehicle Tag Orders
            </button>
            <button
              className={`cat-btn ${category === 'restaurant' ? 'active' : ''}`}
              onClick={() => handleCategoryChange('restaurant')}
            >
              Franchise/Restaurant Orders
            </button>
          </div>
        )}

        <div className="orders-filters">
          {viewMode === 'admin' ? (
            <>
              <button
                className={`filter-btn ${filter === 'active' ? 'active' : ''}`}
                onClick={() => handleAdminTabChange('active')}
              >
                Active Orders
              </button>
              <button
                className={`filter-btn ${filter === 'old' ? 'active' : ''}`}
                onClick={() => handleAdminTabChange('old')}
              >
                Old Orders
              </button>
              <button
                className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
                onClick={() => handleAdminTabChange('all')}
              >
                All Orders
              </button>
            </>
          ) : (
            <>
              <button
                className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
                onClick={() => setFilter('all')}
              >
                All
              </button>
              <button
                className={`filter-btn ${filter === 'pending' ? 'active' : ''}`}
                onClick={() => setFilter('pending')}
              >
                Pending
              </button>
              <button
                className={`filter-btn ${filter === 'completed' ? 'active' : ''}`}
                onClick={() => setFilter('completed')}
              >
                Completed
              </button>
            </>
          )}
        </div>
      </div>

      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading orders...</p>
        </div>
      ) : orders.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <p>No orders found</p>
            <p className="empty-state-subtitle">
              {viewMode === 'admin' ? 'No orders in this category' : 'Your order history will appear here'}
            </p>
          </div>
        </div>
      ) : (
        <>
          {viewMode === 'admin' ? (
            <div className="card admin-orders-table-container">
              <div className="table-responsive">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Order ID</th>
                      <th>Customer</th>
                      <th>{category === 'tag' ? 'Tag Type' : 'Table / Type'}</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentOrders.map(order => (
                      <tr key={order.id}>
                        <td><strong>#{order.id}</strong></td>
                        <td>
                          <div className="customer-info">
                            <span className="customer-name">{order.user_name || 'Guest'}</span>
                            <span className="customer-email">{order.user_email || order.contact_number}</span>
                          </div>
                        </td>
                        <td>
                          {category === 'tag' ? (
                            <span className={`tag-icon-pill ${order.tag_type?.toLowerCase().includes('bike') ? 'bike' : 'car'}`}>
                              {order.tag_type?.toLowerCase().includes('bike') ? '🏍️' : '🚗'} {order.tag_type}
                            </span>
                          ) : (
                            <span className="tag-icon-pill restaurant">
                              🍽️ {order.tag_type || 'Table'}
                            </span>
                          )}
                        </td>
                        <td><strong className="text-primary">₹{order.amount}</strong></td>
                        <td>
                          <span className={`status-badge status-${order.status?.toLowerCase()}`}>
                            {order.status}
                          </span>
                        </td>
                        <td>{formatDate(order.created_at)}</td>
                        <td>
                          <div className="admin-table-actions">
                            {order.status === 'pending' && (
                              <>
                                <button className="btn-icon-small" title="Mark Shipped" onClick={() => handleUpdateStatus(order.id, 'shipped')}>🚢</button>
                                <button className="btn-icon-small primary" title="Mark Delivered" onClick={() => handleUpdateStatus(order.id, 'delivered')}>✅</button>
                              </>
                            )}
                            {order.status === 'shipped' && (
                              <button className="btn-icon-small primary" title="Mark Delivered" onClick={() => handleUpdateStatus(order.id, 'delivered')}>✅</button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="orders-list">
              {currentOrders.map(order => {
                const status = order.status?.toLowerCase() || 'pending';
                let shipping = {};
                try {
                  shipping = typeof order.shipping_address === 'string'
                    ? JSON.parse(order.shipping_address)
                    : (order.shipping_address || {});
                } catch (e) {
                  console.error("Parse error for order address:", e);
                }

                return (
                  <div key={order.id} className="order-card">
                    <div className="order-header-section">
                      <div className="order-meta">
                        <span className="order-id-label">Order #{order.id}</span>
                        <span className="order-date-label">Placed on {formatDate(order.created_at)}</span>
                      </div>
                      <div className={`status-badge status-${status}`}>
                        {status}
                      </div>
                    </div>

                    <div className="order-main-content">
                      <div className="product-info">
                        <div className="product-visual">
                          {order.tag_type?.toLowerCase().includes('bike') ? '🏍️' : '🚗'}
                        </div>
                        <div className="item-text">
                          <span className="item-sku">ITEM SKU: NAH-TAG-{status.toUpperCase()}</span>
                          <h3>{order.tag_type || 'Smart Vehicle Tag'}</h3>
                          <div className="item-total">₹{order.amount}</div>
                        </div>
                      </div>

                      <div className="info-cards-grid">
                        <div className="info-card">
                          <div className="info-card-header">
                            <span>📦</span> Shipping Details
                          </div>
                          <div className="info-card-content">
                            <strong style={{ color: 'white', display: 'block', marginBottom: '4px' }}>
                              {shipping.name || user?.name}
                            </strong>
                            <span className="address-line">{shipping.address || 'Address not found'}</span>
                            <span className="address-line">
                              {shipping.city}{shipping.pincode ? `, ${shipping.pincode}` : ''}
                            </span>
                          </div>
                        </div>

                        <div className="info-card">
                          <div className="info-card-header">
                            <span>📞</span> Contact Support
                          </div>
                          <div className="info-card-content">
                            <div className="detail-row">
                              <span className="detail-label">Mobile</span>
                              <span className="detail-value">{shipping.mobile || order.contact_number || order.user_mobile || 'N/A'}</span>
                            </div>
                            <div className="detail-row">
                              <span className="detail-label">Email</span>
                              <span className="detail-value">{shipping.email || order.user_email || user?.email}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="order-footer-section">
                      <div className="payment-status">
                        <span>🛡️</span> {status === 'cancelled' ? 'Payment Terminated' : 'Securely Paid via PayU'}
                      </div>
                      {order.payment_id && (
                        <div className="payment-ref">
                          Ref: {order.payment_id}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {totalPages > 1 && (
            <div className="pagination-container" style={{ marginTop: '2rem', display: 'flex', justifyContent: 'center', gap: '1rem', alignItems: 'center' }}>
              <button
                className="btn-secondary"
                onClick={() => handlePageChange(Math.max(currentPage - 1, 1))}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              <span className="pagination-info" style={{ color: 'var(--text-secondary)' }}>
                Page {currentPage} of {totalPages} ({totalCount} total)
              </span>
              <button
                className="btn-secondary"
                onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default Orders


